import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild
  // Switch
} from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import trackEvents from '../constants/track-events'
import {
  useAivyContext,
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../context'
import { LayoutFillContext } from '../context/layout-fill-context'
import { SampleDataProvider } from '../context/sample-data-context'
import { isSet } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import { localStorage } from '../helpers/local-storage'
import { SAMPLE_DATA_TOOLTIP_ID } from '../helpers/sample-data'
import { useSidebarShortcut } from '../hooks/use-sidebar-shortcut'
import AccessExpired from './access-expired'
import Announcement from './announcement'
import ChooseGroupSlideOver from './choose-group-slide-over'
import AdminOnboardingSkipChip from './discover/admin-onboarding-skip-chip'
import HelpSupportSlideover from './help-support-slideover'
import AdminPricingChip from './pricing/admin-pricing-chip'
import SampleDataDialog from './sample-data/sample-data-dialog'
import Sidebar from './sidebar'
import SpaceAutoSwitchNotice from './space-auto-switch-notice'

const Layout = ({ children, fillViewport }) => {
  const { t } = useTranslation()

  const { accessExpired } = usePaymentContext()
  const { system, restartDashboard } = useAivyContext()
  const { isAdmin, activeSpace } = useUserContext()
  const { partner } = usePartnerContext()

  const { pathname } = useLocation()

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isVisibleChooseGroup, setVisibilityChooseGroup] = useState(false)
  const [isVisibleAnnouncement, setIsVisibleAnnouncement] = useState(null)
  const [isVisibleHelpAndSupport, setIsVisibleHelpAndSupport] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    isSet(activeSpace?.ui_settings_general?.is_sidebar_collapsed)
      ? activeSpace.ui_settings_general.is_sidebar_collapsed
      : true
  )

  const toggleSidebar = useCallback(() => {
    trackEvent(trackEvents.CLICK_SIDEBAR_COLLAPSE)
    activeSpace.updateUiSettingsGeneral({
      key: 'is_sidebar_collapsed',
      value: !isSidebarCollapsed
    })
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }, [activeSpace, isSidebarCollapsed])

  useSidebarShortcut(toggleSidebar)

  useEffect(() => {
    const { announcement } = system
    if (!announcement) return

    const toDate = new Date(announcement.to_date)
    const fromDate = new Date(announcement.from_date)

    if (announcement.active && new Date() >= fromDate && new Date() <= toDate) {
      setIsVisibleAnnouncement(announcement)
    }
  }, [system])

  const showAccessExpired = accessExpired && pathname !== '/support'
  const showAdminHint = !!localStorage.getItem('admin')
  // Listen-Seiten füllen genau den Viewport, gescrollt wird nur die Tabelle.
  // Kein overflow-hidden in dieser Kette, sonst schneiden die Filter-Popover
  // ab. Reicht die Höhe nicht, scrollt wie bisher das Dokument.
  const fills = fillViewport && !showAccessExpired

  const handleRemoveAdmin = async () => {
    localStorage.removeItem('admin')
    restartDashboard()
  }

  return (
    <SampleDataProvider>
      <div>
        <Transition show={sidebarOpen} as={Fragment}>
          <Dialog
            as='div'
            className='relative z-40 lg:hidden'
            onClose={setSidebarOpen}
          >
            <TransitionChild
              as={Fragment}
              enter='transition-opacity ease-linear duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition-opacity ease-linear duration-300'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-gray-600 bg-opacity-75' />
            </TransitionChild>

            <div className='fixed inset-0 z-40 flex'>
              <TransitionChild
                as={Fragment}
                enter='transition ease-in-out duration-300 transform'
                enterFrom='-translate-x-full'
                enterTo='translate-x-0'
                leave='transition ease-in-out duration-300 transform'
                leaveFrom='translate-x-0'
                leaveTo='-translate-x-full'
              >
                <DialogPanel className='relative flex w-full max-w-xs flex-1 flex-col bg-gray-800'>
                  {/* <TransitionChild
                    as={Fragment}
                    enter='ease-in-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in-out duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  > */}
                  <div className='absolute right-0 top-0 -mr-12 pt-2'>
                    <button
                      type='button'
                      className='ml-1 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100'
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className='sr-only'>Close sidebar</span>
                      <XMarkIcon
                        className='h-6 w-6 text-gray-900'
                        aria-hidden='true'
                      />
                    </button>
                  </div>
                  {/* </TransitionChild> */}
                  <Sidebar
                    actions={{
                      setIsVisibleHelpAndSupport,
                      setVisibilityChooseGroup
                    }}
                  />
                </DialogPanel>
              </TransitionChild>
              <div className='w-14 flex-shrink-0'>
                {/* Force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Static sidebar for desktop */}
        <div
          className={clsx(
            'relative z-20 hidden transition-[width] duration-200 ease-in-out motion-reduce:transition-none lg:fixed lg:inset-y-0 lg:flex lg:flex-col',
            isSidebarCollapsed ? 'lg:w-16' : 'lg:w-72'
          )}
        >
          <Sidebar
            actions={{
              setIsVisibleHelpAndSupport,
              setVisibilityChooseGroup
            }}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleCollapse={toggleSidebar}
          />
          {/* Klickfläche auf der Kante (shadcn: SidebarRail). Liegt etwa da,
              wo früher der runde Pfeil-Button saß, und fängt die gewohnten
              Klicks ab. Per Tastatur geht es über den Button im Kopf. */}
          <button
            type='button'
            tabIndex={-1}
            onClick={toggleSidebar}
            aria-label={t(
              isSidebarCollapsed
                ? 'components.layout.sidebar_expand'
                : 'components.layout.sidebar_collapse'
            )}
            title={t(
              isSidebarCollapsed
                ? 'components.layout.sidebar_expand'
                : 'components.layout.sidebar_collapse'
            )}
            className={clsx(
              'absolute inset-y-0 -right-2 z-30 w-4',
              'after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:transition-colors hover:after:bg-brightBlue/60',
              isSidebarCollapsed ? 'cursor-e-resize' : 'cursor-w-resize'
            )}
          />
        </div>
        <div
          className={clsx(
            'flex flex-1 flex-col transition-[padding] duration-200 ease-in-out motion-reduce:transition-none',
            isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72',
            fills ? 'h-screen supports-[height:100dvh]:h-dvh' : 'min-h-screen'
          )}
        >
          <div className='sticky top-0 z-10 pl-1 pt-1 sm:pl-3 sm:pt-3 lg:hidden'>
            <button
              type='button'
              className='-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-900'
              onClick={() => setSidebarOpen(true)}
            >
              <span className='sr-only'>Open sidebar</span>
              <Bars3Icon className='h-6 w-6' aria-hidden='true' />
            </button>
          </div>
          <main className={clsx('flex-grow', fills && 'flex min-h-0 flex-col')}>
            {isVisibleAnnouncement && (
              <Announcement
                // onClose={() => setIsVisibleAnnouncement(null)}
                type={isVisibleAnnouncement.type}
                text={isVisibleAnnouncement.message}
                action={isVisibleAnnouncement.action}
              />
            )}
            {/* Admin-Banner, Pricing-Simulation und Onboarding-Skip teilen sich
                die Sticky-Leiste */}
            {(isAdmin || showAdminHint) && (
              <div className='sticky top-0 z-50'>
                {showAdminHint && (
                  <Announcement
                    onClose={handleRemoveAdmin}
                    type='ERROR'
                    text={t('components.layout.admin_hint', {
                      partner: (
                        partner.display_name || partner.name
                      ).toUpperCase()
                    })}
                  />
                )}
                {isAdmin && <AdminPricingChip />}
                {isAdmin && <AdminOnboardingSkipChip />}
              </div>
            )}
            <div className={clsx(fills && 'flex min-h-0 flex-1 flex-col')}>
              {showAccessExpired ? (
                <AccessExpired />
              ) : (
                <LayoutFillContext.Provider value={fills}>
                  {children}
                </LayoutFillContext.Provider>
              )}
            </div>
          </main>
        </div>
      </div>

      {isAdmin && (
        <ChooseGroupSlideOver
          open={isVisibleChooseGroup}
          setOpen={setVisibilityChooseGroup}
        />
      )}
      <HelpSupportSlideover
        isVisibleHelpAndSupport={isVisibleHelpAndSupport}
        setIsVisibleHelpAndSupport={setIsVisibleHelpAndSupport}
      />
      {/* Beispieldaten: ein Dialog für alle Einstiege, ein Tooltip für alle Badges */}
      <SampleDataDialog
        onOpenSupport={() => setIsVisibleHelpAndSupport(true)}
      />
      {/* Hinweis nach dem automatischen Wechsel aus einem abgelaufenen Standort */}
      {!accessExpired && <SpaceAutoSwitchNotice />}
      <ReactTooltip
        id={SAMPLE_DATA_TOOLTIP_ID}
        className='z-50 max-w-xs rounded-md text-xs'
        style={{ backgroundColor: '#182033' }}
        place='top'
      />
    </SampleDataProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  fillViewport: PropTypes.bool
}

Layout.defaultProps = {
  fillViewport: false
}

export default Layout
